import type { ShortcutType } from 'lib/keyboardShortcuts';

export type MonarchCommandGroup =
  | 'general'
  | 'settings'
  | 'actions'
  | 'navigation'
  | 'miscellaneous'
  | 'entities';

type MonarchCommandActionOptions = {
  /** Combo used to trigger the command, if applicable. */
  combo: string;
};

export interface MonarchCommand {
  id: string;
  label: string | React.ReactNode;
  group?: MonarchCommandGroup;
  /**
   * The keys to trigger the command.
   * If not provided, the command will not be triggered by any keys.
   *
   * We use Mousetrap to handle the key bindings. More at: https://craig.is/killing/mice
   *
   * @example
   * ```ts
   * ['command+shift+p', 'ctrl+shift+p']
   * ```
   */
  keys?: string[];
  shortcutType?: ShortcutType;
  icon?: React.ReactNode;
  /**
   * The action to perform when the command is triggered.
   */
  action: (options: Partial<MonarchCommandActionOptions>) => void | Promise<void>;
  /**
   * The keywords to improve searchability of the command.
   *
   * @example
   * ```ts
   * ['command', 'palette', 'open']
   * ```
   */
  keywords?: string[];
  /**
   * Additional condition that must be met for the command to be registered.
   */
  shouldRegister?: boolean;
}

export type MonarchAiCommand = MonarchCommand & {
  isAi: true;
  icon: never;
};

export type MonarchEntityCommand = MonarchCommand & {
  type: string;
};

/** Group of commands that are known to the command palette. */
export type MonarchKnownCommand = MonarchCommand | MonarchEntityCommand | MonarchAiCommand;

export function isMonarchAiCommand(command: MonarchKnownCommand): command is MonarchAiCommand {
  return Object.hasOwn(command, 'isAi');
}

export function isMonarchEntityCommand(
  command: MonarchKnownCommand,
): command is MonarchEntityCommand {
  return Object.hasOwn(command, 'type');
}
